import { FC, FormEvent, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import styled, { css } from "styled-components";

import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { Label } from "@/components/Forms";
import { ChannelResource } from "@/types/resources";
import Text from "@/components/Text";

const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 8px;
  margin-top: 16px;
`;

const Option = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 8px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
  ${({ theme, $selected }) =>
    $selected &&
    css`
      border-color: ${theme.colors.purple} !important;
    `}

  ${Text} {
    flex: 1;
    text-align: left;
  }
`;

const Thumb = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  object-fit: cover;
  margin-right: 8px;
`;

interface Props {
  onClose: () => void;
  accountId: number;
}

const ChannelsAttachmentForm: FC<Props> = ({ onClose, accountId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);

  const queryClient = useQueryClient();

  const alerts = useAlerts();

  const { data: channels } = useQuery(
    ["channels", { forMetaBusinessAssigment: true }],
    () =>
      api.get<never, Array<Pick<ChannelResource, "id" | "name" | "thumbnail">>>(
        `/v1/channels/unassigned`
      ),
    {
      refetchOnMount: true,
    }
  );

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    try {
      e.preventDefault();
      setIsSubmitting(true);

      const { invalidChannels } = await api.post<
        object,
        { invalidChannels: string[] }
      >("/v1/meta-business-accounts/channels", {
        channels: selectedChannels,
        meta_business_account_id: accountId,
      });

      await queryClient.invalidateQueries(["meta_business_accounts"]);

      if (invalidChannels.length) {
        alerts.error(
          "Some of the selected pages does not belong to this business account",
          `Invalid pages: ${invalidChannels.join(", ")}`
        );
      }

      onClose();
    } catch (_) {
      alerts.error();
      setIsSubmitting(false);
    }
  };

  const toggleChannel = (channelId: number) => {
    setSelectedChannels((prev) => {
      return prev.includes(channelId)
        ? prev.filter((c) => c !== channelId)
        : [...prev, channelId];
    });
  };

  return (
    <Modal>
      <SidePanel heading="Add page" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <Label as="p">Select Facebook Pages</Label>
            <Options>
              {channels?.map((c) => (
                <Option
                  type="button"
                  $selected={selectedChannels.includes(c.id)}
                  onClick={() => toggleChannel(c.id)}
                >
                  {!!c.thumbnail && <Thumb src={c.thumbnail} />}
                  <Text $type="smallHeadline">{c.name}</Text>
                </Option>
              ))}
            </Options>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Save
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ChannelsAttachmentForm;
